/**
 * The user's date/time display preference: component order, separator, and 12- vs. 24-hour time.
 *
 * This is the first feature in the app to render a date or time anywhere (`git grep -n "toLocale\|
 * Intl\." web/src server/src` returns nothing before this file), which is exactly the trigger point
 * `claude-kit/rules/invariants.md`'s "Every displayed date or time gets a user-configurable format"
 * names: check at the moment a project starts rendering dates, not as a retrofit once several pages
 * already format them their own way. `web/src/schedule/` is the one consumer today.
 *
 * Deliberately shaped like `theme/preference.ts` — pure functions over plain values and a
 * `Storage`-shaped object, stored in `localStorage` rather than a server profile for the same reason
 * that file states (it has to apply before any session exists), read with the identical
 * fall-back-per-field-on-any-failure discipline. This is a *display* preference layered on top of the
 * browser's own local timezone interpretation of a stored UTC instant — it never changes which
 * instant a date names, only how its already-resolved local wall-clock value is printed.
 */

export const DATE_ORDERS = ['MDY', 'DMY', 'YMD'] as const;
export type DateOrder = (typeof DATE_ORDERS)[number];
export function isDateOrder(value: string): value is DateOrder {
  return (DATE_ORDERS as readonly string[]).includes(value);
}

export const DATE_SEPARATORS = ['/', '-', '.'] as const;
export type DateSeparator = (typeof DATE_SEPARATORS)[number];
export function isDateSeparator(value: string): value is DateSeparator {
  return (DATE_SEPARATORS as readonly string[]).includes(value);
}

export const HOUR_CYCLES = ['h12', 'h24'] as const;
export type HourCycle = (typeof HOUR_CYCLES)[number];
export function isHourCycle(value: string): value is HourCycle {
  return (HOUR_CYCLES as readonly string[]).includes(value);
}

export interface DateTimePreference {
  readonly dateOrder: DateOrder;
  readonly separator: DateSeparator;
  readonly hourCycle: HourCycle;
}

export const DEFAULT_DATE_TIME_PREFERENCE: DateTimePreference = {
  dateOrder: 'MDY',
  separator: '/',
  hourCycle: 'h12',
};

/** Three keys rather than one JSON blob, matching `theme/preference.ts`'s own reasoning: a corrupt
 * or partially-written value then costs one field, not the whole preference. */
export const DATE_ORDER_STORAGE_KEY = 'emr:datetime-order';
export const DATE_SEPARATOR_STORAGE_KEY = 'emr:datetime-separator';
export const HOUR_CYCLE_STORAGE_KEY = 'emr:datetime-hourcycle';

/** The subset of `Storage` this module uses — see `theme/preference.ts`'s identical `PreferenceStorage`
 * for why it is narrower than the DOM type. */
export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readDateTimePreference(storage: PreferenceStorage): DateTimePreference {
  let dateOrder: DateOrder = DEFAULT_DATE_TIME_PREFERENCE.dateOrder;
  let separator: DateSeparator = DEFAULT_DATE_TIME_PREFERENCE.separator;
  let hourCycle: HourCycle = DEFAULT_DATE_TIME_PREFERENCE.hourCycle;

  try {
    const storedOrder = storage.getItem(DATE_ORDER_STORAGE_KEY);
    if (storedOrder !== null && isDateOrder(storedOrder)) dateOrder = storedOrder;

    const storedSeparator = storage.getItem(DATE_SEPARATOR_STORAGE_KEY);
    if (storedSeparator !== null && isDateSeparator(storedSeparator)) separator = storedSeparator;

    const storedHourCycle = storage.getItem(HOUR_CYCLE_STORAGE_KEY);
    if (storedHourCycle !== null && isHourCycle(storedHourCycle)) hourCycle = storedHourCycle;
  } catch {
    return DEFAULT_DATE_TIME_PREFERENCE;
  }

  return { dateOrder, separator, hourCycle };
}

/** Persist a preference. A storage failure is not worth failing the interaction over, matching
 * `theme/preference.ts`'s `writePreference`. */
export function writeDateTimePreference(
  storage: PreferenceStorage,
  preference: DateTimePreference,
): void {
  try {
    storage.setItem(DATE_ORDER_STORAGE_KEY, preference.dateOrder);
    storage.setItem(DATE_SEPARATOR_STORAGE_KEY, preference.separator);
    storage.setItem(HOUR_CYCLE_STORAGE_KEY, preference.hourCycle);
  } catch {
    // Non-fatal: the preference still applies for this page's lifetime.
  }
}
